import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../css/PIPForm.css';
import Navbar from './Navbar';
import { addPipDetails, getPreRegDetails } from '../Services/Api';
import { useNavigate } from 'react-router-dom';

// Validation schema
const schema = yup.object().shape({
  orgName: yup.string().required('Organization name is required'),
  orgWebsite: yup
    .string()
    .required('Organization Website is required')
    .url('Please enter a valid website URL'),
  ipAddress: yup
    .string()
    .required('IP address is required')
    .matches(
      /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
      'Please enter a valid IP address'
    ),
  orgAddress: yup.string().required('Address is required'),
  coordinatorName: yup.string().required('Coordinator name is required'),
  designation: yup.string().required('Designation is required'),
  email: yup.string().email('Please enter a valid email').required('Email is required'),
  altEmail: yup.string().email('Please enter a valid email'),
  mobile: yup
    .string()
    .required('Mobile number is required')
    .matches(/^[0-9]{10}$/, 'Mobile number must be 10 digits'),
  altMobile: yup.string().matches(/^[0-9]{10}$/, 'Mobile number must be 10 digits'),
  username: yup.string().required('Username is required'),
  password: yup.string().required('Password is required').min(6, 'Password must be at least 6 characters'),
  gst: yup.string().required('GST selection is required'),
  courses: yup.array().min(1, 'Please select at least one course').required('Course selection is required'),
});

const PIPRegistrationForm = () => {
  const navigate = useNavigate();

  const [courseDetails, setCourseDetails] = useState([]);
  const [userDetails, setUserDetails] = useState([]);
  const [gstDetails, setGstDetails] = useState([]);
  const [serverErrors, setServerErrors] = useState({});
  const [mouFile, setMouFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const getPreRegDetailsFn = async () => {
      try {
        const data = await getPreRegDetails();
        setCourseDetails(data.course || []);
        setUserDetails(data.user || []);
        setGstDetails(data.gst_details || []);
      } catch (err) {
        console.log(err);
      }
    };
    getPreRegDetailsFn();
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    setError,
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      courses: [],
      gst: "",
    },
  });

  const selectedCourses = watch('courses', []);

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  // Updated to handle selection using category_id
  const handleCourseSelection = (course) => {
    let updatedCourses;
    if (selectedCourses.includes(course.category_id)) {
      updatedCourses = selectedCourses.filter((c) => c !== course.category_id);
    } else {
      updatedCourses = [...selectedCourses, course.category_id];
    }
    setValue('courses', updatedCourses);
  };

  const getErrorMessage = (fieldName) => {
    return serverErrors[fieldName]?.[0] || errors[fieldName]?.message;
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setServerErrors({});
    clearErrors(); // Clear previous server errors

    try {
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        if (key === 'courses') {
          data.courses.forEach((courseId) => formData.append('courses[]', courseId));
        } else if (key === 'mou') {
          if (mouFile) formData.append('mou', mouFile);
        } else {
          formData.append(key, data[key]);
        }
      });

      const response = await addPipDetails(formData);
// console.log('data');

      if (response.status === 'success') {
        toast.success('PIP Details Registered successfully!', { autoClose: 3000 });
        resetForm();
      } else if (response.status === 'failure') {
        if (response.errors) {
          setServerErrors(response.errors);
          Object.keys(response.errors).forEach((key) => {
            setError(key, { type: 'server', message: response.errors[key][0] });
          });
        } else if (response.error) {
          const errorMessage = response.error;

          if (errorMessage.includes('Duplicate entry') && errorMessage.includes('upskill_user_emailandphone')) {
            setServerErrors({
              email: ['This email and phone combination already exists in our system'],
              mobile: ['This email and phone combination already exists in our system'],
            });

            setError('email', { type: 'server', message: 'This email and phone combination already exists' });
            setError('mobile', { type: 'server', message: 'This email and phone combination already exists' });
          } else {
            toast.error(response.message || 'A database error occurred. Please try again.');
          }
        } else {
          toast.error(response.message || 'An error occurred during registration.');
        }
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      toast.error('An unexpected error occurred while submitting the form.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    reset();
    setDropdownOpen(false);
    setMouFile(null);
    setServerErrors({});
  };

  const onReset = () => resetForm();

  const handleTest = () => {
    toast.success("PIP Details Registered successfully!", { autoClose: 3000 });
  };

  return (
    <>
      <Navbar />
      <div className='pip-registration-main'>
        <ToastContainer />
        <div className="pip-registration-container">
          <div className="pip-registration-header">
            <h1 className="pip-registration-title">PIP Registration</h1>
            <p className="pip-registration-subtitle">Please fill in all required fields to register PIP details</p>
          </div>

          <form className="pip-registration-body" onSubmit={handleSubmit(onSubmit)} >
            {/* Organisation Details */}
            <div className="pip-registration-section">
              <h3 className="pip-registration-section-title"><b>Organisation Details</b></h3>
              <div className="pip-registration-row">
                <div className="pip-registration-group pip-registration-required">
                  <label htmlFor="orgName" className="pip-registration-label">Organisation Name</label>
                  <input type="text" id="orgName" {...register('orgName')} className={`pip-registration-input ${getErrorMessage('orgName') ? 'error' : ''}`} />
                  {getErrorMessage('orgName') && <span className="pip-registration-error">{getErrorMessage('orgName')}</span>}
                </div>
                <div className="pip-registration-group">
                  <label htmlFor="orgWebsite" className="pip-registration-label">Organisation Website</label>
                  <input type="text" id="orgWebsite" {...register('orgWebsite')} className={`pip-registration-input ${getErrorMessage('orgWebsite') ? 'error' : ''}`} />
                  {getErrorMessage('orgWebsite') && <span className="pip-registration-error">{getErrorMessage('orgWebsite')}</span>}
                </div>
              </div>
              <div className="pip-registration-row">
                <div className="pip-registration-group pip-registration-required">
                  <label htmlFor="ipAddress" className="pip-registration-label">IP Address</label>
                  <input type="text" id="ipAddress" {...register('ipAddress')} className={`pip-registration-input ${getErrorMessage('ipAddress') ? 'error' : ''}`} />
                  {getErrorMessage('ipAddress') && <span className="pip-registration-error">{getErrorMessage('ipAddress')}</span>}
                </div>
                <div className="pip-registration-group pip-registration-required">
                  <label htmlFor="orgAddress" className="pip-registration-label">Address</label>
                  <input type="text" id="orgAddress" {...register('orgAddress')} className={`pip-registration-input ${getErrorMessage('orgAddress') ? 'error' : ''}`} />
                  {getErrorMessage('orgAddress') && <span className="pip-registration-error">{getErrorMessage('orgAddress')}</span>}
                </div>
              </div>
            </div>

            {/* Coordinator Information */}
            <div className="pip-registration-section">
              <h3 className="pip-registration-section-title"><b>Coordinator Information</b></h3>
              <div className="pip-registration-row">
                <div className="pip-registration-group pip-registration-required">
                  <label htmlFor="coordinatorName" className="pip-registration-label">Name</label>
                  <input type="text" id="coordinatorName" {...register('coordinatorName')} className={`pip-registration-input ${getErrorMessage('coordinatorName') ? 'error' : ''}`} />
                  {getErrorMessage('coordinatorName') && <span className="pip-registration-error">{getErrorMessage('coordinatorName')}</span>}
                </div>
                <div className="pip-registration-group">
                  <label htmlFor="designation" className="pip-registration-label">Designation</label>
                  <input type="text" id="designation" {...register('designation')} className={`pip-registration-input ${getErrorMessage('designation') ? 'error' : ''}`} />
                  {getErrorMessage('designation') && <span className="pip-registration-error">{getErrorMessage('designation')}</span>}
                </div>
              </div>

              <div className="pip-registration-row">
                <div className="pip-registration-group pip-registration-required">
                  <label htmlFor="email" className="pip-registration-label">Email ID</label>
                  <input type="email" id="email" {...register('email')} className={`pip-registration-input ${getErrorMessage('email') ? 'error' : ''}`} />
                  {getErrorMessage('email') && <span className="pip-registration-error">{getErrorMessage('email')}</span>}
                </div>
                <div className="pip-registration-group">
                  <label htmlFor="altEmail" className="pip-registration-label">Alternate Email ID</label>
                  <input type="email" id="altEmail" {...register('altEmail')} className={`pip-registration-input ${getErrorMessage('altEmail') ? 'error' : ''}`} />
                  {getErrorMessage('altEmail') && <span className="pip-registration-error">{getErrorMessage('altEmail')}</span>}
                </div>
                <div className="pip-registration-group pip-registration-required">
                  <label htmlFor="mobile" className="pip-registration-label">Mobile No</label>
                  <input type="tel" id="mobile" {...register('mobile')} className={`pip-registration-input ${getErrorMessage('mobile') ? 'error' : ''}`} />
                  {getErrorMessage('mobile') && <span className="pip-registration-error">{getErrorMessage('mobile')}</span>}
                </div>
              </div>

              <div className="pip-registration-row">
                <div className="pip-registration-group">
                  <label htmlFor="altMobile" className="pip-registration-label">Alternate Mobile No</label>
                  <input type="tel" id="altMobile" {...register('altMobile')} className={`pip-registration-input ${getErrorMessage('altMobile') ? 'error' : ''}`} />
                  {getErrorMessage('altMobile') && <span className="pip-registration-error">{getErrorMessage('altMobile')}</span>}
                </div>

                {/* Username Auto-fill */}
                {userDetails?.map((users) => (
                  <div key={users.id} className="pip-registration-group pip-registration-required">
                    <label htmlFor="username" className="pip-registration-label">Username</label>
                    <input
                      type="text"
                      id="username"
                      value={`PIP_${new Date().getFullYear()}${users.id + 1}`}
                      {...register('username')}
                      className={`pip-registration-input ${getErrorMessage('username') ? 'error' : ''}`}
                      readOnly
                    />
                    {getErrorMessage('username') && <span className="pip-registration-error">{getErrorMessage('username')}</span>}
                  </div>
                ))}

                {/* Password */}
                <div className="pip-registration-group pip-registration-required">
                  <label htmlFor="password" className="pip-registration-label">Password</label>
                  <input type="password" id="password" {...register('password')} className={`pip-registration-input ${getErrorMessage('password') ? 'error' : ''}`} />
                  {getErrorMessage('password') && <span className="pip-registration-error">{getErrorMessage('password')}</span>}
                </div>

                {/* GST */}
                <div className="pip-registration-group pip-registration-required">
                  <label htmlFor="gst" className="pip-registration-label">GST</label>
                  <select id="gst" {...register('gst')} className={`pip-registration-input ${getErrorMessage('gst') ? 'error' : ''}`}>
                    <option value="">-- Select GST Option --</option>
                    {gstDetails.map((gst) => (
                      <option key={gst.gst_id} value={gst.gst_flag_name}>{gst.gst_flag_name}</option>
                    ))}
                  </select>
                  {getErrorMessage('gst') && <span className="pip-registration-error">{getErrorMessage('gst')}</span>}
                </div>
              </div>
            </div>

            {/* Course Selection */}
            <div className="pip-registration-section">
              <h3 className="pip-registration-section-title"><b>Course Information</b></h3>
              <div className="pip-registration-row">
                <div className="pip-registration-group pip-registration-required">
                  <label className="pip-registration-label">Select Courses</label>

                  <div className="custom-dropdown-container">
                    <div className={`custom-dropdown-header ${dropdownOpen ? 'open' : ''} ${getErrorMessage('courses') ? 'error' : ''}`} onClick={toggleDropdown}>
                      {selectedCourses.length > 0 ? `${selectedCourses.length} course(s) selected` : 'Select courses'}
                      <span className="dropdown-arrow">▼</span>
                    </div>

                    {dropdownOpen && (
                      <div className="custom-dropdown-options">
                        {courseDetails.map((course) => (
                          <div key={course.category_id} className={`custom-dropdown-option ${selectedCourses.includes(course.category_id) ? 'selected' : ''}`} onClick={() => handleCourseSelection(course)}>
                            <input type="checkbox" checked={selectedCourses.includes(course.category_id)} readOnly className="custom-checkbox" />
                            {course.course_name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {getErrorMessage('courses') && <span className="pip-registration-error">{getErrorMessage('courses')}</span>}

                  {selectedCourses?.length > 0 && (
                    <div className="pip-registration-selected-courses">
                      <p className="pip-registration-selected-title">Selected Courses:</p>
                      <ul className="pip-registration-selected-list">
                        {selectedCourses.map((courseId, idx) => {
                          const course = courseDetails.find((c) => c.category_id === courseId);
                          return (
                            <li key={idx} className="pip-registration-selected-item">
                              {course ? course.course_name : courseId}
                              <button type="button" className="remove-course-btn" onClick={() => handleCourseSelection({ category_id: courseId })}>×</button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="pip-registration-footer">
              <button type="button" onClick={onReset} className="pip-registration-btn pip-registration-btn-reset">Reset Form</button>
              <button type="submit" className="pip-registration-btn pip-registration-btn-submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default PIPRegistrationForm;
